<template>
  <div>
    <div class="flex items-center justify-between mb-6 max-md:flex-col max-md:items-start max-md:gap-3">
      <div class="flex items-center gap-2.5">
        <h1 class="text-[26px] font-bold text-ink -tracking-[0.03em] m-0">Activity</h1>
        <span v-if="filteredActivities.length" class="bg-[#f0eef5] text-primary text-[13px] font-semibold py-0.5 px-2.5 rounded-full">{{ filteredActivities.length }}</span>
      </div>
      <div class="search-box flex items-center gap-2 bg-[#fafafa] border border-black/6 rounded-xl px-3.5 h-10 min-w-[220px] transition-[border-color,background] duration-150 text-placeholder max-md:w-full max-md:min-w-0">
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search activity..."
          class="border-none outline-none bg-transparent text-[13px] text-ink w-full h-full placeholder:text-placeholder">
      </div>
    </div>

    <skeleton-activities v-if="isLoading"></skeleton-activities>

    <div v-if="!isLoading && filteredActivities.length === 0" class="flex flex-col items-center py-[60px] px-5 text-muted">
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
      <p class="text-sm text-muted mt-4 mb-0">{{ searchQuery ? 'No matching activity' : 'No activity yet' }}</p>
    </div>

    <div v-else>
      <div v-for="(group, date) in groupedActivities" :key="date" class="mb-7 last:mb-0">
        <div class="text-[11px] font-bold uppercase tracking-[0.06em] text-[#a8a3ac] mb-2.5 px-1">{{ date }}</div>
        <div class="bg-white border border-black/6 rounded-[20px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden">
          <div
            v-for="activity in group"
            :key="activity.id"
            class="group flex gap-3 py-3.5 px-5 border-b border-black/4 last:border-b-0">

            <img v-if="getActivityPhoto(activity)" :src="getActivityPhoto(activity)" class="shrink-0 w-[30px] h-[30px] rounded-full object-cover mt-0.5">
            <div v-else class="shrink-0 w-[30px] h-[30px] rounded-full bg-[#f0eef5] text-primary flex items-center justify-center font-bold text-xs mt-0.5">{{ activity.user ? activity.user.charAt(0) : '?' }}</div>

            <div class="flex-1 min-w-0">
              <div class="text-sm text-ink leading-snug">
                <strong v-if="activity.user" class="font-semibold mr-1">{{ activity.user }}</strong>
                <span>{{ activity.message }}</span>
              </div>
              <p v-if="activity.details" class="text-[13px] text-[#645B66] mt-1.5 mb-0 leading-relaxed bg-canvas py-2.5 px-3.5 rounded-lg whitespace-pre-wrap break-words" v-html="linkify(activity.details)"></p>
              <div class="flex items-center gap-2.5 mt-1.5">
                <router-link v-if="getApplicantName(activity.applicantId)" :to="{ name: 'admin-applicant', params: { id: activity.applicantId }}" class="text-xs font-medium text-primary no-underline hover:underline hover:text-primary">
                  {{ getApplicantName(activity.applicantId) }}
                </router-link>
                <span class="text-xs text-placeholder">{{ formatTimeAgo(activity.createdAt) }}</span>
              </div>
            </div>

            <button
              v-if="user && user.isAdmin"
              class="shrink-0 bg-transparent border-0 text-muted cursor-pointer p-1 rounded-md opacity-0 transition-[opacity,color] duration-150 self-start mt-0.5 group-hover:opacity-100 hover:!text-[#e53935]"
              :class="{ '!opacity-100 !text-[#e53935] text-xs font-semibold': confirmDeleteId === activity.id }"
              @click.stop="deleteActivity(activity)">
              <span v-if="confirmDeleteId === activity.id">Delete?</span>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SkeletonActivities from '@/components/UIComponents/SkeletonActivities'
import { mapState } from 'vuex'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { PHOTOS } from '@/services/photos'
dayjs.extend(relativeTime)

export default {
  name: 'all-activities',
  components: { SkeletonActivities },
  data () {
    return {
      isLoading: false,
      unsubscribe: null,
      confirmDeleteId: null,
      searchQuery: ''
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      allActivities: state => state.activities.allActivities,
      applicants: state => state.applicants.applicants
    }),
    filteredActivities () {
      if (!this.searchQuery.trim()) return this.allActivities
      const q = this.searchQuery.toLowerCase().trim()
      return this.allActivities.filter(activity => {
        const user = (activity.user || '').toLowerCase()
        const message = (activity.message || '').toLowerCase()
        const details = (activity.details || '').toLowerCase()
        const applicantName = (this.getApplicantName(activity.applicantId) || '').toLowerCase()
        return user.includes(q) || message.includes(q) || details.includes(q) || applicantName.includes(q)
      })
    },
    groupedActivities () {
      const groups = {}
      this.filteredActivities.forEach(activity => {
        const date = this.formatDate(activity.createdAt)
        if (!groups[date]) {
          groups[date] = []
        }
        groups[date].push(activity)
      })
      return groups
    }
  },
  methods: {
    getActivityPhoto (activity) {
      if (!activity.user) return null
      return PHOTOS[activity.user.toLowerCase()] || null
    },
    formatTimeAgo (date) {
      if (!date) return ''
      return dayjs(date).fromNow()
    },
    formatDate (date) {
      if (!date) return 'Unknown'
      const d = dayjs(date)
      const today = dayjs()
      if (d.isSame(today, 'day')) return 'Today'
      if (d.isSame(today.subtract(1, 'day'), 'day')) return 'Yesterday'
      return d.format('MMM D, YYYY')
    },
    deleteActivity (activity) {
      if (this.confirmDeleteId === activity.id) {
        this.confirmDeleteId = null
        this.$store.dispatch('DELETE_ACTIVITY', {
          applicantId: activity.applicantId,
          activityId: activity.id
        })
      } else {
        this.confirmDeleteId = activity.id
      }
    },
    linkify (text) {
      if (!text) return ''
      const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      return escaped.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" class="text-primary underline hover:opacity-80">$1</a>')
    },
    getApplicantName (applicantId) {
      if (!applicantId || !this.applicants) return null
      const applicant = this.applicants.find(a => a.id === applicantId)
      if (!applicant) return null
      return applicant.firstName + ' ' + applicant.lastName
    }
  },
  created () {
    this.isLoading = true
    this.$store.dispatch('GET_ALL_ACTIVITIES').then((unsub) => {
      this.unsubscribe = unsub
      this.isLoading = false
    }).catch((error) => {
      this.isLoading = false
      console.error('Failed to load activities:', error)
    })

    if (this.user && !this.user.isAdmin) {
      this.$router.push({ name: 'admin-applicants' })
    }

    this._resetConfirm = () => { this.confirmDeleteId = null }
    document.addEventListener('click', this._resetConfirm)
  },
  beforeDestroy () {
    if (this.unsubscribe && typeof this.unsubscribe === 'function') {
      this.unsubscribe()
    }
    document.removeEventListener('click', this._resetConfirm)
  }
}
</script>

<style scoped>
.search-box:focus-within {
  border-color: #7c5bea;
  background: #fff;
}

.search-box:focus-within svg {
  color: #7c5bea;
}
</style>
